import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Student } from "@/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowLeft, Plus, Activity, FileText, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { MonitoringReadingsTable } from "./MonitoringReadingsTable";
import { TakeReadingDialog } from "./TakeReadingDialog";
import { StudentVisitDialog } from "@/components/nurse/StudentVisitDialog";
import { MonitoringTrends } from "./MonitoringTrends";
import { GenerateReportDialog } from "./GenerateReportDialog";

interface MonitoringDashboardProps {
  student: Student;
  onClose: () => void;
  onCreateProfile: () => void;
}

export function MonitoringDashboard({ student, onClose, onCreateProfile }: MonitoringDashboardProps) {
  const [showTakeReading, setShowTakeReading] = useState(false);
  const [showTreatDialog, setShowTreatDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: currentTerm } = useQuery({
    queryKey: ['currentTerm'],
    queryFn: async () => {
      const { data: activeYear } = await supabase
        .from('academic_years')
        .select('id')
        .eq('is_active', true)
        .single();

      if (!activeYear) return null;

      const { data: term } = await supabase
        .from('terms')
        .select('*')
        .eq('is_active', true)
        .eq('academic_year_id', activeYear.id)
        .single();

      return term;
    }
  });

  const { data: profile, isLoading } = useQuery({
    queryKey: ['monitoringProfile', student.id, currentTerm?.id],
    queryFn: async () => {
      if (!currentTerm?.id) return null;

      const { data } = await supabase
        .from('special_monitoring_profiles')
        .select('*')
        .eq('student_id', student.id)
        .eq('term_id', currentTerm.id)
        .eq('is_active', true)
        .single();

      return data;
    },
    enabled: !!currentTerm?.id
  });

  const { data: readings = [] } = useQuery({
    queryKey: ['monitoringReadings', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      const { data } = await supabase
        .from('special_monitoring_readings')
        .select('*')
        .eq('profile_id', profile.id)
        .order('recorded_at', { ascending: false });

      return data || [];
    },
    enabled: !!profile?.id
  });

  const handleVisitSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['visits'] });
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Search
        </Button>
      </div>

      {!profile ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {student.surname}, {student.other_names}
            </CardTitle>
            <CardDescription>
              {student.class} {student.stream} • Special Monitoring Profile
            </CardDescription>
          </CardHeader>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="text-muted-foreground">
                No monitoring profile found for this term. Create one to start tracking specialized readings.
              </div>
              <Button onClick={onCreateProfile}>
                <Plus className="h-4 w-4 mr-2" />
                Create Monitoring Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {student.surname}, {student.other_names}
            </CardTitle>
            <CardDescription>
              {student.class} {student.stream} • Special Monitoring Profile
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={() => setShowTakeReading(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Take Reading
              </Button>
              <Button variant="outline" onClick={() => setShowTreatDialog(true)}>
                <Activity className="h-4 w-4 mr-2" />
                Record Regular Visit
              </Button>
              <Button variant="outline" onClick={() => setShowReportDialog(true)}>
                <FileText className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            </div>

            <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
              <CollapsibleTrigger className="w-full">
                <div className="flex flex-row items-center justify-between hover:bg-accent/50 transition-colors rounded-lg p-4 border">
                  <h3 className="font-semibold text-lg">Monitoring Details</h3>
                  <ChevronDown className={`h-5 w-5 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-6 pt-6">
                  {/* Parameters Section */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3">Parameters</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {(profile.parameters as any[])?.map((param: any, index: number) => (
                        <div key={index} className="border rounded-lg p-3">
                          <div className="font-medium">{param.name}</div>
                          <div className="text-sm text-muted-foreground">
                            Range: {param.min} - {param.max} {param.unit}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Trends Section */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3">Trends</h3>
                    <MonitoringTrends profile={profile} readings={readings} />
                  </div>

                  {/* Reading History Section */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3">Reading History</h3>
                    <MonitoringReadingsTable 
                      profile={profile} 
                      readings={readings}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>

          <TakeReadingDialog
            open={showTakeReading}
            onOpenChange={setShowTakeReading}
            profile={profile}
          />

          {showReportDialog && (
            <GenerateReportDialog
              open={showReportDialog}
              onOpenChange={setShowReportDialog}
              student={student}
              profile={profile}
              readings={readings}
            />
          )}
        </Card>
      )}

      <StudentVisitDialog
        student={student}
        open={showTreatDialog}
        onOpenChange={setShowTreatDialog}
        onVisitSaved={handleVisitSaved}
      />
    </div>
  );
}
